import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import LiveTicker from '../components/LiveTicker';
import LiveBadge from '../components/LiveBadge';
import { Play, Clock, CheckCircle, Calendar, Eye, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format time until kickoff
const formatTimeUntil = (isoTime) => {
  if (!isoTime) return 'TBD';
  const kickoff = new Date(isoTime);
  const now = new Date();
  const diff = kickoff - now;
  
  if (diff < 0) return 'Starting soon';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

// Format kickoff time
const formatKickoff = (isoTime) => {
  if (!isoTime) return 'TBD';
  const date = new Date(isoTime);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Match Card Component
const MatchCard = ({ match, status }) => {
  const teams = match.teams?.split(' vs ') || ['TBD', 'TBD'];
  const homeTeam = teams[0]?.trim() || 'TBD';
  const awayTeam = teams[1]?.trim() || 'TBD';

  return (
    <Link 
      to={`/match/${match.id}`}
      className="block group"
      data-testid={`match-card-${match.id}`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        className={`
          relative overflow-hidden rounded-xl transition-all duration-300
          ${status === 'live' 
            ? 'bg-gradient-to-br from-red-900/30 to-[#1a1a1a] border border-red-500/30 hover:border-red-500/60' 
            : status === 'upcoming'
            ? 'bg-gradient-to-br from-emerald-900/20 to-[#1a1a1a] border border-emerald-500/20 hover:border-emerald-500/40'
            : 'bg-[#1a1a1a] border border-white/10 hover:border-white/20'
          }
        `}
      >
        {/* Thumbnail */}
        <div className="relative aspect-video">
          <img 
            src={match.thumbnail_url} 
            alt={match.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* Status Badge */}
          {status === 'live' && (
            <div className="absolute top-3 left-3">
              <LiveBadge variant="default" />
            </div>
          )}
          
          {status === 'upcoming' && (
            <div className="absolute top-3 left-3 bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              {formatTimeUntil(match.start_time)}
            </div>
          )}
          
          {status === 'finished' && (
            <div className="absolute top-3 left-3 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1.5">
              <CheckCircle className="w-3 h-3" />
              FT
            </div>
          )}

          {/* Premium Badge */}
          {match.is_premium && (
            <div className="absolute top-3 right-3 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded">
              PREMIUM
            </div>
          )}

          {/* Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
              <Play className="w-8 h-8 text-white fill-white" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Competition */}
          <div className="text-emerald-500 text-xs font-semibold uppercase tracking-wide mb-2">
            {match.competition_name || 'Match'}
          </div>

          {/* Teams */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <img 
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(homeTeam)}&background=0D8ABC&color=fff&size=32`}
                  alt={homeTeam}
                  className="w-8 h-8 rounded-full"
                />
                <span className="text-white font-semibold truncate">{homeTeam}</span>
              </div>
            </div>
            
            <div className="px-4 text-center">
              {status === 'live' || status === 'finished' ? (
                <span className="text-white font-bold text-lg">vs</span>
              ) : (
                <span className="text-white/50 text-sm">{formatKickoff(match.start_time)}</span>
              )}
            </div>
            
            <div className="flex-1 text-right">
              <div className="flex items-center gap-3 justify-end">
                <span className="text-white font-semibold truncate">{awayTeam}</span>
                <img 
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(awayTeam)}&background=D35400&color=fff&size=32`}
                  alt={awayTeam}
                  className="w-8 h-8 rounded-full"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-white/50 text-xs">
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span>{match.views || 0} watching</span>
            </div>
            <div className="flex items-center gap-1 text-emerald-500 group-hover:text-emerald-400">
              <span>Watch</span>
              <ChevronRight className="w-3 h-3 transform group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

// Section Component
const Section = ({ title, icon: Icon, matches, status, emptyMessage }) => {
  if (matches.length === 0) return null;

  return (
    <section className="mb-12" data-testid={`section-${status}`}>
      <div className="flex items-center gap-3 mb-6">
        {status === 'live' && (
          <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        )}
        <Icon className={`w-6 h-6 ${status === 'live' ? 'text-red-500' : status === 'upcoming' ? 'text-emerald-500' : 'text-white/60'}`} />
        <h2 className="text-2xl font-bold text-white">{title}</h2>
        <span className="text-white/40 text-sm">({matches.length})</span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {matches.map((match) => (
          <MatchCard key={match.id} match={match} status={status} />
        ))}
      </div>
    </section>
  );
};

export default function Live() {
  const [liveMatches, setLiveMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [finishedMatches, setFinishedMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
    // Poll every 30 seconds
    const interval = setInterval(fetchContent, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchContent = async () => {
    try {
      // Fetch live content
      const liveResponse = await axios.get(`${API}/content/live`);
      setLiveMatches(liveResponse.data);
      
      // Fetch all content for upcoming and finished
      const allResponse = await axios.get(`${API}/content/featured`);
      const now = new Date();
      
      // Filter upcoming (has start_time in the future and not live)
      const upcoming = allResponse.data
        .filter(c => !c.is_live && c.start_time && new Date(c.start_time) > now)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      setUpcomingMatches(upcoming);
      
      // Filter finished/replays (VOD content or past matches)
      const finished = allResponse.data
        .filter(c => !c.is_live && c.content_type === 'vod')
        .slice(0, 6);
      setFinishedMatches(finished);
      
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="watch-page">
      <Navbar />
      <LiveTicker />

      <main className="pt-28 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" data-testid="watch-title">
              Watch
            </h1>
            <p className="text-white/60 text-lg">
              Today's matches and live events from across Africa
            </p>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="loading-state">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
            </div>
          ) : (
            <>
              {/* Live Now Section */}
              <Section 
                title="Live Now" 
                icon={Play} 
                matches={liveMatches} 
                status="live"
                emptyMessage="No live matches at the moment"
              />

              {/* Starting Soon Section */}
              <Section 
                title="Starting Soon" 
                icon={Clock} 
                matches={upcomingMatches} 
                status="upcoming"
                emptyMessage="No upcoming matches scheduled"
              />

              {/* Finished / Replays Section */}
              <Section 
                title="Recent Replays" 
                icon={CheckCircle} 
                matches={finishedMatches} 
                status="finished"
                emptyMessage="No recent replays available"
              />

              {/* Empty State */}
              {liveMatches.length === 0 && upcomingMatches.length === 0 && finishedMatches.length === 0 && (
                <div className="text-center py-20" data-testid="empty-state">
                  <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Calendar className="w-10 h-10 text-white/30" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">No Matches Today</h3>
                  <p className="text-white/60 max-w-md mx-auto">
                    Check back later for upcoming live matches and events from African football.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
